/**
 * typeDefaultRegistry — a tiny module-level cache mapping an object TYPE to its
 * curated default GLB ref (`{ modelId, modelUrl }`) or `null`.
 *
 * Separate from glbModelService's per-category model cache: this stores the
 * resolved "ephemeral default" decision keyed by render type. Reads are sync so
 * the render path (resolveRenderSource) can consult it without async/Suspense.
 *
 * A `null` entry is a NEGATIVE cache: "asked, and there is no default" (or the
 * default GLB failed to load). Distinguish "no entry yet" (undefined → not
 * looked up) from "entry is null" (looked up, none available) via `has`.
 */

const cache = new Map();

/** Has this type been looked up yet (entry may be null)? */
export const hasTypeDefaultEntry = (type) => cache.has(type);

/**
 * Sync read of a type's default ref. Returns `{ modelId, modelUrl }` or `null`
 * ("no default OR not loaded yet"). resolveRenderSource treats null as "fall
 * through to procedural", which gives the procedural warm-up for free.
 */
export const getTypeDefaultSync = (type) => cache.get(type) || null;

/** Store a resolved default ref (or null) for a type. */
export const setTypeDefault = (type, ref) => {
  cache.set(type, ref || null);
};

/**
 * Mark a type's default as unavailable (negative cache). Used when a default
 * GLB fails to load so the resolver drops the object to procedural/fallback and
 * we never retry the broken URL this session.
 */
export const markDefaultUnavailable = (type) => {
  cache.set(type, null);
};

/** Clear the whole registry (invalidation — called alongside clearGLBModelCache). */
export const clearTypeDefaultRegistry = () => {
  cache.clear();
};
